import { useEffect } from 'react';

interface ShortcutHandlers {
  onFocusSearch: () => void;
  onTogglePalette: () => void;
  onSave?: () => void;
}

/** True when the user is typing somewhere that should keep the keystroke. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    // CodeMirror's editable surface.
    target.closest('.cm-editor') !== null
  );
}

/**
 * Global keyboard shortcuts.
 *
 * `/` only focuses search when the user is not already typing — otherwise it would be impossible
 * to put a slash in a snippet. Cmd/Ctrl+K and Cmd/Ctrl+S work anywhere, since both replace a
 * browser default that is useless here.
 */
export function useShortcuts({ onFocusSearch, onTogglePalette, onSave }: ShortcutHandlers) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const modifier = event.metaKey || event.ctrlKey;

      if (modifier && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onTogglePalette();
        return;
      }

      if (modifier && event.key.toLowerCase() === 's') {
        event.preventDefault();
        onSave?.();
        return;
      }

      if (event.key === '/' && !modifier && !isTypingTarget(event.target)) {
        event.preventDefault();
        onFocusSearch();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onFocusSearch, onTogglePalette, onSave]);
}
